import React, { useState, useEffect, useRef } from 'react';
import { Badge } from 'react-bootstrap';  // Import Bootstrap Badge
import { Link, useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './leaveStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const getStatusBadge = (status) => {
    let variant;
    switch (status) {
        case 'Rejected':
            variant = 'danger';
            break;
        case 'Pending':
            variant = 'primary';
            break;
        case 'Approved':
            variant = 'success';
            break;
        default:
            variant = 'secondary';
    }
    return <Badge bg={variant}>{status}</Badge>;
};

const Statuses = {
    Pending: 0,
    Approved: 1,
    Rejected: 2
};

const LeaveList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [itemsPerPage] = useState(10);
    const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
    const [leaveData, setLeaveData] = useState([]);
    const [selectedLeaveIds, setSelectedLeaveIds] = useState([]);
    const [dropdownStates, setDropdownStates] = useState({});
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingAction, setPendingAction] = useState('');
    const navigate = useNavigate();
    const selectAllRef = useRef(null);
    const [statusFilters, setStatusFilters] = useState({
        Pending: true,
        Approved: true,
        Rejected: true,
    });

    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    const toggleDropdown = (itemId) => {
        setDropdownStates((prevState) => ({
            ...prevState,
            [itemId]: !prevState[itemId],
        }));
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setStatusFilters({
            ...statusFilters,
            [name]: checked,
        });
    };

    const fetchEmployeeNameList = async () => {
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${apiUrl}/Employee/Namelist`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                const employee = result.Employee && result.Employee.length > 0 ? result.Employee[0] : null;
                setCurrentEmployeeId(employee ? employee.EmployeeID : null);
            }
        } catch (error) {
            setCurrentEmployeeId(null);
        }
    };

    const handleLeaveSelection = (id, checked) => {
        setSelectedLeaveIds((prevIds) => {
            if (checked) {
                return prevIds.includes(id) ? prevIds : [...prevIds, id];
            }

            return prevIds.filter((selectedId) => selectedId !== id);
        });
    };

    const fetchLeaveData = async (searchTerm) => {
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');

        const employeeQueryString = currentEmployeeId && !userAccess.hasApprover ? `&empID=${currentEmployeeId}` : '';
        let apiUrlEndpoint = `${apiUrl}/Leave?searchTerm=${searchTerm}&${statusQueryString}${employeeQueryString}&pageNumber=${currentPage}&pageSize=${itemsPerPage}`;
        if (userAccess.hasApprover) {
            apiUrlEndpoint = `${apiUrl}/Leave/ForApproval?searchTerm=${searchTerm}&pageNumber=${currentPage}&pageSize=${itemsPerPage}`;
        }
        try {
            const response = await fetch(apiUrlEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                setLeaveData(result.Data || []);
                setTotalPages(result.TotalPages || 0);
                setSelectedLeaveIds([]);
            } else {
                setErrorMessage('Unable to load leave records.');
                setSuccessMessage('');
            }
        } catch (error) {
            setErrorMessage('Unable to load leave records.');
            setSuccessMessage('');
        }
        setIsLoading(false);
    };

    useEffect(() => {
        // Set a timeout to debounce the input change
        const timeoutId = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
        }, 1); // to delay the fetchData 1 milliseconds after trigger search

        // Clear the timeout on every input change
        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        if (!userAccess.hasApprover) {
            fetchEmployeeNameList();
        }
    }, []);

    useEffect(() => {
        // Fetch data with the debounced search term
        if (!userAccess.hasApprover && !currentEmployeeId) {
            return;
        }
        fetchLeaveData(debouncedSearchInput);
    }, [debouncedSearchInput, statusFilters, currentPage, itemsPerPage, currentEmployeeId]);

    const currentItems = leaveData;
    const selectedCurrentItemsCount = currentItems.filter((item) => selectedLeaveIds.includes(item.ID)).length;
    const areAllCurrentItemsSelected = currentItems.length > 1 && selectedCurrentItemsCount === currentItems.length;
    const hasMeaningfulBulkSelection = selectedCurrentItemsCount >= 2;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = hasMeaningfulBulkSelection && !areAllCurrentItemsSelected;
        }
    }, [areAllCurrentItemsSelected, hasMeaningfulBulkSelection]);

    const updateLeaveStatus = async (ids, status) => {
        const token = localStorage.getItem('token');
        const actionLabel = status === 'Approved' ? 'approval' : 'rejection';
        const endpointAction = status === 'Approved' ? 'Approve' : 'Reject';
        setIsSubmitting(true);
        setPendingAction(status);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const responses = await Promise.all(
                ids.map(async (id) => {
                    const leaveRecord = leaveData.find((item) => item.ID === id);
                    const response = await fetch(`${apiUrl}/Leave/${endpointAction}/${id}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            Remarks: leaveRecord?.Remarks ?? '',
                        }),
                    });

                    let data = null;
                    try {
                        data = await response.json();
                    } catch (parseError) {
                        data = null;
                    }

                    return { response, data };
                })
            );

            const failedResponse = responses.find(({ response }) => !response.ok);
            if (failedResponse) {
                setErrorMessage(failedResponse.data?.Message || `Unable to complete the ${actionLabel} request.`);
                return;
            }

            setSuccessMessage(status === 'Approved' ? 'Successfully Approved' : 'Successfully Rejected');
            setSelectedLeaveIds([]);
            setTimeout(async () => {
                setSuccessMessage('');
                await fetchLeaveData(debouncedSearchInput);
            }, 3000);
        } catch (error) {
            setErrorMessage(`Unable to complete the ${actionLabel} request.`);
        } finally {
            setIsSubmitting(false);
            setPendingAction('');
        }
    };

    const handleLeaveApproval = async (status) => {
        if (selectedLeaveIds.length === 0) {
            return;
        }

        await updateLeaveStatus(selectedLeaveIds, status);
    };

    const handleSelectAllLeave = () => {
        const currentItemIds = currentItems.map((item) => item.ID);
        setSelectedLeaveIds((prevIds) => {
            const hasAnySelectedCurrentItems = currentItemIds.some((id) => prevIds.includes(id));

            if (hasAnySelectedCurrentItems) {
                return prevIds.filter((id) => !currentItemIds.includes(id));
            }

            const nextIds = [...prevIds];
            currentItemIds.forEach((id) => {
                if (!nextIds.includes(id)) {
                    nextIds.push(id);
                }
            });
            return nextIds;
        });
    };

    return (
        <div>
            {(userAccess.hasView) ? (
                <>
                    <h3>Leave Listing</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    {(userAccess.hasStandard || userAccess.hasAdmin) && (
                        <Link to='/leave-create' className='btn btn-primary float-end mb-2'>Add leave</Link>
                    )}

                    <div className='d-flex align-items-start mb-2'>
                        <div className='custom-width'>
                            <input
                                type='text'
                                placeholder='Search..'
                                className='form-control'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>

                        {!hasApprover && (
                            <div className="accordion ms-2" id="accordionFilter">
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFilter">
                                        <button
                                            className="accordion-button"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#collapseFilter"
                                            aria-expanded="true"
                                            aria-controls="collapseFilter"
                                            style={{ height: '35px' }}
                                        >
                                            Status
                                        </button>
                                    </h2>
                                    <div
                                        id="collapseFilter"
                                        className="accordion-collapse collapse position-absolute"
                                        aria-labelledby="headingFilter"
                                        data-bs-parent="#accordionFilter"
                                    >
                                        <div className="accordion-body">
                                            <div className="accordion" id="nestedAccordion">
                                                <div className="accordion-item border-2">
                                                    <div
                                                        id="collapseStatusFilter"
                                                        className="accordion-collapse collapse show"
                                                        aria-labelledby="headingStatusFilter"
                                                        data-bs-parent="#nestedAccordion"
                                                    >
                                                        <div className="accordion-body">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="pendingCheckbox"
                                                                    name="Pending"
                                                                    checked={statusFilters.Pending}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="pendingCheckbox">
                                                                    Pending
                                                                </label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="approvedCheckbox"
                                                                    name="Approved"
                                                                    checked={statusFilters.Approved}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="approvedCheckbox">
                                                                    Approved
                                                                </label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="rejectedCheckbox"
                                                                    name="Rejected"
                                                                    checked={statusFilters.Rejected}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="rejectedCheckbox">
                                                                    Rejected
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {hasApprover && selectedLeaveIds.length > 0 && (
                            <>
                                <button
                                    type='button'
                                    className='btn btn-success ms-2'
                                    disabled={selectedLeaveIds.length === 0 || isSubmitting}
                                    onClick={() => handleLeaveApproval('Approved')}
                                >
                                    {isSubmitting && pendingAction === 'Approved' ? 'Approving...' : 'Approve'}
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-danger ms-2'
                                    disabled={selectedLeaveIds.length === 0 || isSubmitting}
                                    onClick={() => handleLeaveApproval('Rejected')}
                                >
                                    {isSubmitting && pendingAction === 'Rejected' ? 'Rejecting...' : 'Reject'}
                                </button>
                            </>
                        )}
                    </div>

                    <table className='custom-table'>
                        <thead>
                            <tr>
                                <th className='border-end-0'></th>
                                {hasApprover && (
                                    <th className='border-start-0 border-end-0 text-center'>
                                        <input
                                            ref={selectAllRef}
                                            type='checkbox'
                                            className='form-check-input'
                                            checked={areAllCurrentItemsSelected}
                                            onChange={handleSelectAllLeave}
                                        />
                                    </th>
                                )}
                                <th className='border-start-0 border-end-0'>Transaction ID</th>
                                <th className='border-start-0 border-end-0'>Transaction Date</th>
                                <th className='border-start-0 border-end-0'>Employee</th>
                                <th className='border-start-0 border-end-0'>Leave Type</th>
                                <th className='border-start-0 border-end-0'>Date Start</th>
                                <th className='border-start-0 border-end-0'>Date End</th>
                                <th className='border-start-0 border-end-0'>Duration</th>
                                <th className='border-start-0'>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={hasApprover ? 10 : 9}>
                                        <div className="d-flex align-items-center">
                                            <div className="spinner-border me-2" aria-hidden="true"></div>
                                            <span role="status"> Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                            currentItems && currentItems.length > 0 ? (
                                currentItems.map((item) => (
                                    <React.Fragment key={item.ID}>
                                        <tr>
                                            <td className="text-sm-center remove-space border-end-0" style={{borderBottom: 'none'}}>
                                                <div className="dropdown">
                                                    <button
                                                        className="btn"
                                                        type="button"
                                                        id={`dropdownMenuButton${item.ID}`}
                                                        data-bs-toggle="dropdown"
                                                        data-bs-auto-close="outside"
                                                        aria-haspopup="true"
                                                        aria-expanded={dropdownStates[item.ID]}
                                                        onClick={() => toggleDropdown(item.ID)}
                                                    >
                                                        <i className="fas fa-ellipsis"></i>
                                                    </button>
                                                    <div
                                                        className={`dropdown-menu ${dropdownStates[item.ID] ? 'show' : ''}`}
                                                        aria-labelledby={`dropdownMenuButton${item.ID}`}
                                                    >
                                                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                                                            <Link to={`/leave-update/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Update
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasView || userAccess.hasAdmin) && (
                                                            <Link to={`/leave-view/${item.ID}`} className="dropdown-item">
                                                                View
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                                                            <Link to={`/leave-delete/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Delete
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasApprover || userAccess.hasAdmin) && (
                                                            <Link to={`/leave-approve/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Approve
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasApprover || userAccess.hasAdmin) && (
                                                            <Link to={`/leave-reject/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Reject
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            {hasApprover && (
                                                <td className='border-start-0 border-end-0 text-center align-middle' style={{ borderBottom: 'none' }}>
                                                    <input
                                                        type='checkbox'
                                                        className='form-check-input'
                                                        checked={selectedLeaveIds.includes(item.ID)}
                                                        onChange={(event) => handleLeaveSelection(item.ID, event.target.checked)}
                                                    />
                                                </td>
                                            )}
                                            <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                            <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                            <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                            <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.LeaveType}</td>
                                            <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                            <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                            <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                            <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={hasApprover ? 10 : 9} style={{ borderTop: 'none', paddingLeft: hasApprover ? '105px' : '65px', maxWidth: '50px' }}>
                                                <strong>Remarks:</strong> {item.Remarks}
                                            </td>
                                    </tr>
                                </React.Fragment>
                            ))
                            ) : (
                                <tr>
                                    <td colSpan={hasApprover ? 10 : 9}>No Record</td>
                                </tr>
                            )
                            )}
                        </tbody>
                    </table>
                    <nav aria-label="Page navigation">
                        <ul className="pagination mt-3">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(1)}
                  aria-label="First"
                >
                  <i className="fas fa-angle-double-left" aria-hidden="true"></i>
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} aria-label="Previous">
                  <i className="fas fa-angle-left" aria-hidden="true"></i>
                </button>
              </li>
                            {(() => {
                                const pageGroupSize = 5;
                                const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
                                const startPage = currentGroup * pageGroupSize + 1;
                                const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

                                const visiblePages = Array.from(
                                    { length: endPage - startPage + 1 },
                                    (_, index) => startPage + index
                                );

                                return visiblePages.map((page) => (
                                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(page)}>
                                        {page}
                                    </button>
                                </li>
                                ));
                            })()}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} aria-label="Next">
                  <i className="fas fa-angle-right" aria-hidden="true"></i>
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(totalPages)}
                  aria-label="Last"
                >
                  <i className="fas fa-angle-double-right" aria-hidden="true"></i>
                </button>
              </li>
                        </ul>
                    </nav>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    );
};

export default LeaveList;
